import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom'
import { GetGroupStudents, GetGroupAssignmentRoles, GroupUserRequestState } from '../../../store/GroupAction';
import * as Helper from '../../../util/Helper';
import StatusCode from '../../../util/StatusCode';
import ActiveToggle from '../../misc/ActiveToggle';
import TableHorizontalPagedToggle, { InfoObject } from '../../misc/TableHorizontalPagedToggle';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';



const GroupViewMembers = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { groupId } = useParams();
    const dispatch = useDispatch();
    const [show, setShow] = useState({ students: true, assignmentRoles: true });

    const students = useSelector<RootState, GroupUserRequestState>(state => state.groups.students);
    const assignmentRoles = useSelector<RootState, GroupUserRequestState>(state => state.groups.assignmentRoles);

    useEffect(() => {
        if (groupId) {
            GetGroupStudents(groupId, { ...students?.filter })(dispatch);
            GetGroupAssignmentRoles(groupId, { ...assignmentRoles?.filter })(dispatch);
        }
    }, [dispatch, groupId]);

    const updateStudentsPage = (value: number) => {
        if (groupId) {
            GetGroupStudents(groupId, { ...students.filter, page: value })(dispatch);
        }
    }

    const updateAssignmentRolesPage = (value: number) => {
        if (groupId) {
            GetGroupAssignmentRoles(groupId, { ...assignmentRoles.filter, page: value })(dispatch);
        }
    }

    const updateOnlyActive = (value: boolean) => {
        if (groupId) {
            GetGroupStudents(groupId, { ...students.filter, onlyActive: value, page: 1 })(dispatch);
            GetGroupAssignmentRoles(groupId, { ...assignmentRoles.filter, onlyActive: value, page: 1 })(dispatch);
        }
    }

    const mapStudents = (): InfoObject[] => {
        if (students.code !== StatusCode.COMPLETE) {
            return [];
        }

        return students.data.values.map(x => ({
            id: x.id,
            data: [
                { value: <Link to={`/user/${x.id}/information`}>{x.title}</Link> },
                { value: x.emailAddresses ? x.emailAddresses.map(e => e).join(', ') : '' },
                { value: Helper.active(x.active) },
            ]
        }))
    }

    const mapAssignmentRoles = (): InfoObject[] => {
        if (assignmentRoles.code !== StatusCode.COMPLETE) {
            return [];
        }

        return assignmentRoles.data.values.map(x => ({
            id: x.id,
            data: [
                { value: <Link to={`/user/${x.id}/information`}>{x.title}</Link> },
                { value: t('assignmentRoleTypes.' + x.assignmentRoleType, { ns: 'schoolAdministration' }) },
                { value: x.emailAddresses ? x.emailAddresses.map(e => e).join(', ') : '' },
                { value: Helper.active(x.active) },
            ]
        }))
    }

    const renderStudents = () => {
        const studentInfo = mapStudents();
        const count = Helper.getTotalCountText(students);
        return (
            <TableHorizontalPagedToggle
                header={[{ value: `${t('commonTableHeaders.students', { ns: 'schoolAdministration' })} ${count}` }, { value: t('commonTableHeaders.e-postaddress', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }]}
                mobileHeader={[t('genericTableHeaders.name', { ns: 'common' }), t('commonTableHeaders.e-postaddress', { ns: 'schoolAdministration' }), t('genericTableHeaders.active', { ns: 'common' })]}
                info={studentInfo}
                show={show.students}
                toggle={() => setShow({ ...show, students: !show.students })}
                displayName={`${t('commonTableHeaders.students', {ns: 'schoolAdministration'})} ${count}`}
                onChangePage={updateStudentsPage}
                noResultMsg={t('noResult.groupStudents', {ns: 'schoolAdministration'})}
                requestState={students}
            />
        )
    }

    const renderAssignmentRoles = () => {
        const assignmentRoleInfo = mapAssignmentRoles();
        const count = Helper.getTotalCountText(assignmentRoles);
        return (
            <TableHorizontalPagedToggle
                header={[{ value: `${t('commonTableHeaders.staff', { ns: 'schoolAdministration' })} ${count}` }, { value: t('commonTableHeaders.role', { ns: 'schoolAdministration' }) }, { value: t('commonTableHeaders.e-postaddress', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }]}
                mobileHeader={[t('genericTableHeaders.name', { ns: 'common' }), t('commonTableHeaders.role', { ns: 'schoolAdministration' }), t('commonTableHeaders.e-postaddress', { ns: 'schoolAdministration' }), t('genericTableHeaders.active', { ns: 'common' })]}
                info={assignmentRoleInfo}
                show={show.assignmentRoles}
                toggle={() => setShow({ ...show, assignmentRoles: !show.assignmentRoles })}
                displayName={`${t('commonTableHeaders.staff', { ns: 'schoolAdministration' })} ${count}`}
                onChangePage={updateAssignmentRolesPage}
                noResultMsg={t('noResult.groupStaff', { ns: 'schoolAdministration' })}
                requestState={assignmentRoles}
            />
        )
    }

    return (<>
        <div className="header_member_table large">{t('commonTableHeaders.members', { ns: 'schoolAdministration' })}
            <span className="right">
                <ActiveToggle
                    id="group-members"
                    onlyActive={students?.filter?.onlyActive ?? true}
                    update={updateOnlyActive}
                    disabled={students.code === StatusCode.PENDING || assignmentRoles.code === StatusCode.PENDING}
                />
            </span></div>
        {renderAssignmentRoles()}
        {renderStudents()}
    </>);
}

export default GroupViewMembers;
